import AdditionalNotes from './sections/additional-notes'
import Consultation from './sections/consultation'
import ContactPatientSection from './sections/contact-patient-section'
import Footer from './sections/footer'
import LinkedPatientData from './sections/linked-patient-data'
import NavigationMenu from './sections/nav-menu'
import OnHoldHistory from './sections/on-hold-history'
import PatientData from './sections/patient-data'
import PatientFiles from './sections/patient-files'
import PrescriptionDetails from './sections/prescription-details'
import PreviousOrders from './sections/previous-orders'
import RejectionReasonSection from './sections/rejection-reason-section'
import WeightLossTracker from './sections/weight-loss-tracker'

import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import { useSuspenseCurrentMedication } from '@/hooks/useCurrentMedication'
import { useLinkedPatients } from '@/services/linkedPatients'
import { useMedicationRequestStore } from '@/lib/stores/medication-request'
import { cn, getCurrentUKDate, isMedRequestInEditableStage } from '@/lib/utils'
import { readOnlyReasonOf } from '@/lib/med-requests'
import { orderPinnedFirst, type PinnableSection, usePinnedSections } from '@/lib/stores/pinned-sections'
import { isWeightLossOrder } from '@/lib/weight-tracker'
import PostOrderView from './post-order-view'
import ApprovalReasonSection from './sections/approval-reason-section'
import ClinicalDecisionPanel, {
  useIsClinicalDecisionPanelOpen
} from './sections/multi-product-order/clinical-decision-panel'
import Header from './sections/header'
import SCRDataSection from './sections/scr-data'
import PreviousInvestigations from './sections/previous-investigations'
import SafeguardingQuestions from './sections/safeguarding-questions'
import { useEffect, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import {
  isRoleAllowedToActionMedRequest,
  isRoleAllowedToEditMedRequest,
  isRoleAllowedToEditMedRequestNotes,
  isRoleLimitedToEditingNotes,
  isCSViewer,
  canViewConversationsWhenLocked
} from '@/lib/permissions'
import { usePosthogCustom } from '@/hooks/usePosthogCustom'

const MedicationRequestPage = () => {
  const { sidebarView, status, setMedicationIssueDate, clearTreatmentDecisions } = useMedicationRequestStore()

  const { trackMedRequestViewed } = usePosthogCustom()

  const { id } = useParams<{ id: string }>()

  const { data } = useSuspenseCurrentMedication()
  const pinnedSections = usePinnedSections(data.id)

  useEffect(() => {
    setMedicationIssueDate(getCurrentUKDate())
    // The open panel and the cards' recorded decisions belong to the previous request.
    clearTreatmentDecisions()
    if (id) trackMedRequestViewed(id)
  }, [id])

  const isInEditableStage = isMedRequestInEditableStage(data.stage)
  const readOnlyReason = readOnlyReasonOf(data)
  const isActionable = readOnlyReason === null

  const isEditable = isActionable && isRoleAllowedToEditMedRequest(data)
  // An order waiting on its other items is read-only as a whole, notes included.
  const isNotesEditable =
    readOnlyReason !== 'awaiting-items' && isInEditableStage && isRoleAllowedToEditMedRequestNotes(data)
  const isNotesOnlyEditor = isNotesEditable && isRoleLimitedToEditingNotes()
  const isCSViewerRole = isCSViewer()

  const success = status !== 'pending'

  const isDecisionPanelOpen = useIsClinicalDecisionPanelOpen(data, isEditable)

  const hideFooter =
    sidebarView !== 'navigation-menu' ||
    (!isCSViewerRole &&
      !canViewConversationsWhenLocked() &&
      !isNotesOnlyEditor &&
      (!isEditable || !isRoleAllowedToActionMedRequest(data)))

  // Handle batch-approval scroll to top.
  useEffect(() => {
    if (status === 'approved') {
      window.scrollTo(0, 0)
    }
  }, [status])

  // Pinned sections lead (below the header) in their regular order; the rest keep theirs.
  // Stable keys let React move a section without remounting it.
  const sections: Array<{ key: PinnableSection | 'weight-loss-tracker'; fallback: string; content: ReactNode }> = [
    { key: 'patient-data', fallback: 'Error Loading Patient Data', content: <PatientDataSection /> },
    {
      key: 'safeguarding-questions',
      fallback: 'Error Loading Safeguarding Questions',
      content: <SafeguardingQuestions isEditable={isEditable} />
    },
    {
      key: 'previous-orders',
      fallback: 'Error Loading Previous Orders',
      content: <PreviousOrders patientId={data.patient.id} />
    },
    {
      key: 'previous-investigations',
      fallback: 'Error Loading Previous Investigations',
      content: <PreviousInvestigations patientId={data.patient.id} />
    },
    { key: 'on-hold-history', fallback: 'Error loading On Hold History', content: <OnHoldHistory /> },
    { key: 'consultation', fallback: 'Error loading Consultation Data', content: <Consultation /> },
    { key: 'patient-files', fallback: 'Error loading Patient Files', content: <PatientFiles /> },
    {
      key: 'weight-loss-tracker',
      fallback: 'Error loading Weight Loss Tracker',
      content: isWeightLossOrder(data) && <WeightLossTracker medRequest={data} />
    },
    { key: 'scr-data', fallback: 'Error loading SCR Data', content: !data.OTC && <SCRDataSection /> },
    {
      key: 'prescription-details',
      fallback: 'Error loading Prescription Details',
      content: <PrescriptionDetails isEditable={isEditable} />
    },
    {
      key: 'additional-notes',
      fallback: 'Error loading Additional Notes',
      content: <AdditionalNotes isEditable={isNotesEditable} />
    }
  ]
  const pinned = new Set<string>(pinnedSections)
  const orderedSections = orderPinnedFirst(sections, section => pinned.has(section.key))

  if (success) return <PostOrderView />

  return (
    <section key={data.id} className='flex flex-col gap-8'>
      <ErrorBoundary fallback='Error Loading Header'>
        <Header readOnlyReason={readOnlyReason} />
      </ErrorBoundary>

      <div className='flex gap-[37px] flex-nowrap w-full justify-between'>
        <div className='flex flex-col gap-14 min-w-0 flex-1'>
          {/* ErrorBoundary renders no wrapper element, so a hidden section (content `false`) adds no gap. */}
          {orderedSections.map(({ key, fallback, content }) => (
            <ErrorBoundary key={key} fallback={fallback}>
              {content}
            </ErrorBoundary>
          ))}
        </div>
        <div
          className={cn(
            'sticky top-[88px] self-start shrink-0',
            // The panel sits under the navigation menu, so the column scrolls above the footer.
            isDecisionPanelOpen && 'max-h-[calc(100vh-208px)] overflow-y-auto pb-4'
          )}
        >
          {sidebarView === 'navigation-menu' && (
            <ErrorBoundary fallback='Error loading Navigation Menu'>
              <NavigationMenu patientId={data.patient.id} showPinToggles />
            </ErrorBoundary>
          )}
          {isDecisionPanelOpen && (
            <ErrorBoundary fallback='Error loading Clinical Decision'>
              <ClinicalDecisionPanel />
            </ErrorBoundary>
          )}
          <ErrorBoundary fallback='Error loading Sidebar Sections'>
            {sidebarView === 'approval-reason' && <ApprovalReasonSection />}
            {sidebarView === 'contact-patient' && <ContactPatientSection />}
            {sidebarView === 'rejection' && <RejectionReasonSection />}
          </ErrorBoundary>
        </div>
      </div>

      {!hideFooter && (
        <ErrorBoundary fallback='Error loading Footer'>
          <Footer />
        </ErrorBoundary>
      )}
    </section>
  )
}

/**
 * Renders LinkedPatientData when linked patients exist, otherwise falls back
 * to the standard single-patient PatientData view.
 *
 * Uses `linkedPatientHash` from the med request to decide which view to show.
 * While the linked patients query is loading, the standard view is shown.
 */
function PatientDataSection() {
  const { data } = useSuspenseCurrentMedication()
  const linkedPatientHash = data?.patient?.linkedPatientHash

  const { data: linkedData, isLoading } = useLinkedPatients(linkedPatientHash)

  // Show the linked comparison view only when we have confirmed linked patients
  // (excluding the current patient). Otherwise, fall back to the standard view.
  const hasLinkedOthers =
    !isLoading && !!linkedData?.patients && linkedData.patients.filter(lp => lp.hash !== data.patient.id).length > 0

  if (hasLinkedOthers) return <LinkedPatientData />
  return <PatientData />
}

export default MedicationRequestPage
